import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { Global } from '@iupics-manager/models/global-var';
import { listenOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import { ZIndexHandlerService } from '@iupics/apiz-grid';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DomHandler } from 'primeng/dom';
import { ProgressBarModule } from 'primeng/progressbar';
import { ZIndexUtils } from 'primeng/utils';
@Component({
  selector: 'iu-modal-ui',
  templateUrl: './modal-ui.component.html',
  styleUrls: ['./modal-ui.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    ProgressBarModule,
    ButtonModule,
    TranslateModule,
    CdkDrag,
    CdkDragHandle,
  ],
})
export default class ModalUiComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title: string;
  @Input() hasCloseBtn = false;
  /** false: a click outside does not close (content with its own Close, e.g. unsaved changes to confirm) */
  @Input() closeOnBackdrop = true;
  @Input() hasFooter = false;
  @Input() closeBtnLabel = 'generic.close';
  @Input() angularStyle: any;
  @Input() isSearch = false;
  @Input() isModalDisplay = true;
  @Input() modalClass = 'p-col-10 p-md-10 p-lg-10';
  @Input() isLoading = false;
  @Input() appendTo = null;
  @ViewChild('container', { static: true }) containerViewChild: ElementRef;
  @Output() closeModalEmitter = new EventEmitter<any>();
  @Input() contentType: ModalContentType;
  #zIndexHandlerService = inject(ZIndexHandlerService);
  isCloseInit = false;
  #closeListeners: (() => void)[] = [];
  baseZIndex = 0;
  private static cpt = 0;
  readonly titleId = 'iu-modal-title-' + ++ModalUiComponent.cpt;
  readonly #keydown = listenOutsideAngular(
    'keydown',
    (event) => event.key === 'Escape' && this.isModalDisplay && this.closeOnBackdrop,
    (event) => this.onDocumentKeydown(event)
  );
  /** Mobile device or narrow window: shown as a bottom sheet, not draggable. */
  isCompact =
    Global.isMobile() || (typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches);
  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    ZIndexUtils.set('modal', this.containerViewChild?.nativeElement, 0);
    this.#zIndexHandlerService.zIndexes.set(this.containerViewChild?.nativeElement, {
      key: this.containerViewChild?.nativeElement,
      value: ZIndexUtils.get(this.containerViewChild?.nativeElement),
    });
  }

  ngAfterViewInit() {
    if (this.appendTo) {
      if (this.appendTo === 'body') {
        Global.activeModals.push(this);
        document.body.appendChild(this.containerViewChild?.nativeElement);
      } else {
        DomHandler.appendChild(this.containerViewChild?.nativeElement, this.appendTo);
      }
    }
  }

  ngOnDestroy() {
    this.#clearCloseListeners();
    if (Global.activeModals && Global.activeModals.length > 0) {
      const index = Global.activeModals.indexOf(this);
      if (index > -1) {
        Global.activeModals.splice(index, 1);
      }
    }
    if (this.isModalDisplay) {
      this.isModalDisplay = false;
      this.closeModalEmitter.emit();
    }

    ZIndexUtils.clear(this.containerViewChild?.nativeElement);
    this.#zIndexHandlerService.clear(this.containerViewChild?.nativeElement);
    if (this.appendTo) {
      this.el?.nativeElement.appendChild(this.containerViewChild?.nativeElement);
    }
  }

  close($event: Event = null, forceClose = false) {
    if (forceClose || !$event || ($event.target as any).classList.contains('iu-modal-background') === true) {
      if ($event) {
        $event.stopPropagation();
      }
      if (this.isModalDisplay) {
        this.isModalDisplay = false;
      }
      ZIndexUtils.clear(this.containerViewChild?.nativeElement);
      this.closeModalEmitter.emit();
    }
  }

  /**
   * Escape closes the top open modal, like a click on its backdrop. Not while a question is open, not when
   * the modal closes only with its own button, not while typing or with a dropdown open in it.
   */
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.defaultPrevented || !this.isModalDisplay || !this.closeOnBackdrop) return;
    if (Global.infoDialog?.isOpen) return;
    const target = event.target as HTMLElement;
    if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    if (document.querySelector('.p-autocomplete-panel, .p-dropdown-panel, .p-multiselect-panel, .p-overlaypanel')) return;
    const container = this.containerViewChild?.nativeElement as HTMLElement;
    if (!container?.isConnected || !this.#isTopModal(container)) return;
    event.preventDefault();
    this.close(event, true);
  }

  #isTopModal(container: HTMLElement): boolean {
    let top: HTMLElement = null;
    let topZ = -Infinity;
    document.querySelectorAll<HTMLElement>('.iu-modal-background').forEach((el) => {
      if (el.hidden || el.classList.contains('hidden') || !el.isConnected) return;
      const z = parseInt(el.style.zIndex || getComputedStyle(el).zIndex, 10) || 0;
      if (z >= topZ) {
        topZ = z;
        top = el;
      }
    });
    return top === container;
  }

  initClose($event: MouseEvent | TouchEvent) {
    const backdrop = this.containerViewChild?.nativeElement;
    // Outside close only for a primary press on this modal's own backdrop (not content, not a nested modal)
    if (!this.closeOnBackdrop || this.isCloseInit || !backdrop || $event.target !== backdrop) {
      return;
    }
    if (typeof MouseEvent !== 'undefined' && $event instanceof MouseEvent && $event.button !== 0) {
      return;
    }
    this.isCloseInit = true;
    const selectionAtPress = this.#selectionText();
    const end = ($e: MouseEvent | TouchEvent) => {
      this.#clearCloseListeners();
      // Release must also be on the backdrop, with no text selection made by this gesture
      const selection = this.#selectionText();
      if (this.#releaseTarget($e) === backdrop && (selection === '' || selection === selectionAtPress)) {
        this.close($e, true);
      }
    };
    this.#closeListeners = [
      this.renderer.listen(document.documentElement, 'mouseup', end),
      this.renderer.listen(document.documentElement, 'touchend', end),
      this.renderer.listen(document.documentElement, 'touchcancel', () => this.#clearCloseListeners()),
      this.renderer.listen(document.documentElement, 'dragstart', () => this.#clearCloseListeners()),
    ];
  }

  #clearCloseListeners() {
    this.#closeListeners.forEach((unlisten) => unlisten());
    this.#closeListeners = [];
    this.isCloseInit = false;
  }

  /** touchend targets the start element: resolve the element under the finger instead. */
  #releaseTarget($e: MouseEvent | TouchEvent): EventTarget | null {
    if (typeof TouchEvent !== 'undefined' && $e instanceof TouchEvent) {
      const touch = $e.changedTouches?.[0];
      return touch ? document.elementFromPoint(touch.clientX, touch.clientY) : null;
    }
    return $e.target;
  }

  #selectionText(): string {
    const selection = typeof window !== 'undefined' ? window.getSelection?.() : null;
    return selection && !selection.isCollapsed ? selection.toString() : '';
  }
}

export type ModalContentType = 'window' | 'process' | 'form' | 'joinFiles';
export const ModalContentType: Record<Uppercase<ModalContentType>, ModalContentType> = {
  WINDOW: 'window',
  PROCESS: 'process',
  FORM: 'form',
  JOINFILES: 'joinFiles',
} as const;
